import React, { useImperativeHandle, forwardRef } from "react"

import ResizableElementWrapper from "../shared/ResizableElementWrapper.component"
import TableVariableSelector from "./TableVariableSelector.component"
import TableDisplayArea from "./TableDisplayArea.component"
import { useTableContextMenu } from "./TableContextMenu.component"
import { useTableElement } from "features/Reporting/hooks/Elements/useTableElement"

import {
  DEFAULT_TABLE_WIDTH,
  DEFAULT_TABLE_HEIGHT,
} from "constants/dashboardElementSizes"

import {
  SlideElement,
  DataElementConfig,
} from "types/ReportingDashboard/elements.types"
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import { EngineQueryResult } from "types/engineOutput.types"
import { ElementRef } from "../../ReportingDisplay.component"

/**
 * Element update payload for table elements
 */
interface TableElementUpdate {
  width?: number
  height?: number
}

/**
 * Props for TableElement component
 */
export interface TableElementProps {
  /** Global filter string passed to data elements */
  filter?: string
  /** Element object to render */
  element: SlideElement
  /** Data structure for variable selection */
  codebook: CodebookData
  /** Instance ID for engine queries */
  queryInstance: QueryInstance | null
  /** Callback when element properties change */
  onUpdate: (elementId: string, updates: TableElementUpdate) => void
  /** Configuration for table element */
  config?: DataElementConfig | null
  /** Callback for config updates */
  onConfigUpdate: (config: DataElementConfig | null) => void
  /** Callback when element deletion is requested */
  onDeleteRequest?: () => void
  /** Callback for updating runtime cache */
  onTableDataUpdate?: (
    elementId: string,
    data: EngineQueryResult | null
  ) => void
  /** Sequential execution request handler */
  onElementExecutionRequest?: (
    elementId: string,
    executeFunction: () => Promise<void>,
    reason?: string
  ) => void
  /** Element registration handler */
  onRegisterElementRef?: (elementId: string, ref: ElementRef | null) => void
}

/**
 * Table Element Component (Phase 2 Refactored with Business Logic Hook)
 *
 * A refactored table component using:
 * - useTableElement: Custom hook containing all business logic
 * - TableVariableSelector: Handles variable selection UI
 * - TableDisplayArea: Handles table rendering and display states
 * - TableContextMenu: Handles context menu operations
 *
 * This demonstrates Phase 2 decomposition approach with extracted business logic.
 * Reduces the component from 989 lines to ~200 lines while maintaining all functionality.
 */
const TableElement = forwardRef<ElementRef, TableElementProps>((props, ref) => {
  // Use the custom hook to get all business logic and state
  const tableLogic = useTableElement(props)

  // Destructure what we need from the hook
  const {
    // State
    tableData,
    convertedTableData,
    showSelects,
    isLoading,
    isQueuedForExecution,
    error,
    hasAttemptedLoad,
    selectOptions,

    // Config values
    selectedOperators,
    crossTabVariables,
    isTransposed,
    tableOperationType,

    // Hooks
    configHook,
    operatorManagement,

    // Handlers
    handleRefresh,
    handleEdit,
    handleDelete,
    handleToggleTranspose,
    handleVariableSelect,
    handleOperatorChange,
    handleTableOperationTypeChange,
    handleGenerate,
    requestSequentialExecution,

    // Imperative handle values
    hasDataAndShouldRefresh,

    // Refs and utilities
    tableContainerRef,
  } = tableLogic

  // Expose methods via useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      hasDataAndShouldRefresh,
      requestSequentialExecution,
    }),
    [hasDataAndShouldRefresh, requestSequentialExecution]
  )

  // Context menu
  const { handleContextMenu, ContextMenu } = useTableContextMenu({
    hasData: !!tableData,
    showSelects,
    isLoading,
    isQueuedForExecution,
    isTransposed,
    onDeleteRequest: props.onDeleteRequest,
    onEdit: handleEdit,
    onRefresh: handleRefresh,
    onDelete: handleDelete,
    onToggleTranspose: handleToggleTranspose,
  })

  // Styles
  const wrapperAdditionalClasses = `relative bg-white bg-opacity-90 rounded-md shadow-sm hover:shadow-md transition-shadow ${
    showSelects ? "inline-block" : ""
  }`

  const wrapperStyleOverrides: React.CSSProperties = {
    minWidth: "280px",
    overflow: "visible",
    borderRadius: "4px",
    boxShadow:
      tableData && !showSelects ? "0 4px 8px rgba(0, 0, 0, 0.1)" : "none",
    ...(props.element.y >= 810
      ? {
          backgroundColor: "white",
          border: "1px solid #e2e8f0",
          margin: "0 auto",
        }
      : {}),
    ...(showSelects ? { width: "350px", height: "auto" } : { height: "auto" }),
  }

  const contentContainerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    padding: "0.25rem",
    position: "relative",
    display: "flex",
    flexDirection: "column",
  }

  const tableHandlesConfig = {
    horizontal: true,
    vertical: false,
    corner: false,
  }

  return (
    <ResizableElementWrapper
      element={props.element}
      onUpdate={props.onUpdate}
      readonly={showSelects}
      minDimensions={{ width: 400, height: 100 }}
      defaultWidth={DEFAULT_TABLE_WIDTH}
      defaultHeight={DEFAULT_TABLE_HEIGHT}
      setSkipUpdateFlag={() => configHook.setSkipNextConfigUpdate(true)}
      additionalClasses={wrapperAdditionalClasses}
      styleOverrides={wrapperStyleOverrides}
      handleStyleType="visible"
      handlesConfiguration={tableHandlesConfig}
    >
      <div
        data-element-id={props.element.id}
        style={contentContainerStyle}
        onContextMenu={handleContextMenu}
      >
        {showSelects && (
          <TableVariableSelector
            selectOptions={selectOptions}
            crossTabVariables={crossTabVariables}
            selectedOperators={selectedOperators}
            availableOperatorOptions={
              operatorManagement.availableStatisticOptions
            }
            tableOperationType={tableOperationType}
            queryInstance={!!props.queryInstance}
            isLoading={isLoading}
            isQueuedForExecution={isQueuedForExecution}
            onVariableSelect={handleVariableSelect}
            onOperatorChange={handleOperatorChange}
            onTableOperationTypeChange={handleTableOperationTypeChange}
            onGenerate={handleGenerate}
          />
        )}

        {!showSelects && (
          <TableDisplayArea
            tableData={tableData}
            convertedTableData={convertedTableData}
            error={error}
            isLoading={isLoading}
            isQueuedForExecution={isQueuedForExecution}
            isInitializing={configHook.isInitializing}
            hasAttemptedLoad={hasAttemptedLoad}
            elementWidth={props.element.width}
            tableContainerRef={tableContainerRef}
            onRefresh={handleRefresh}
          />
        )}
      </div>

      {/* Context menu */}
      {ContextMenu}
    </ResizableElementWrapper>
  )
})

TableElement.displayName = "TableElement"

export default TableElement
